<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatventas/utils';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import OtpInput from 'shared/components/OtpInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  account_deactivated: 'LOGIN.API.ACCOUNT_DEACTIVATED',
  logged_in_elsewhere: 'LOGIN.API.LOGGED_IN_ELSEWHERE',
};

// Error codes que merecen un banner persistente (no un toast transitorio).
// Son mensajes que el usuario necesita poder leer con calma porque explican
// por qué fue expulsado de la app.
const PERSISTENT_ERROR_CODES = ['account_deactivated', 'logged_in_elsewhere'];

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    OtpInput,
    NextButton,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      otpCode: '',
      showOtpStep: false,
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      isDarkMode: false,
      // Banner persistente arriba del formulario (a diferencia del toast).
      // Se renderiza cuando authError requiere atención sostenida — p.ej.
      // `account_deactivated` después de que un admin desactiva la cuenta.
      persistentNotice: null,
      // Estado D1 (single session): backend respondió 409 al primer intento
      // porque hay otra sesión activa. Si esto queda seteado, el form muestra
      // el mensaje + botón de "Cerrar sesión en el otro dispositivo".
      sessionConflict: null,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    showGoogleOAuth() {
      return Boolean(window.chatventasConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.chatventasConfig.signupEnabled);
    },
  },
  created() {
    // Inicializar dark mode desde preferencia del sistema o localStorage
    const savedTheme = localStorage.getItem('login_theme');
    if (
      savedTheme === 'dark' ||
      (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ) {
      this.isDarkMode = true;
      document.documentElement.classList.add('dark');
    }
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const message = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      // Para errores destacados (`account_deactivated`, `logged_in_elsewhere`)
      // mostramos un banner persistente sobre el formulario en vez del toast
      // transitorio, porque el usuario necesita ver y leer el mensaje sin prisa.
      if (PERSISTENT_ERROR_CODES.includes(this.authError)) {
        this.persistentNotice = { key: this.authError, messageKey: message };
      } else {
        useAlert(this.$t(message));
      }
      // wait for idle state — limpia ?error=... de la URL sin recargar para
      // que un refresh posterior no vuelva a disparar el banner.
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    async submitLogin({ forceLogout = false } = {}) {
      // Guard against double-submit from auto-complete + Enter key race
      if (this.loginApi.showLoading) return;
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      if (this.showOtpStep) {
        credentials.otp_code = this.otpCode;
      }

      // Single-session override: si el backend respondió 409
      // `session_already_active`, el usuario puede reintentar aceptando
      // expulsar la sesión previa. Se pasa `force_logout=true` al /auth/sign_in.
      if (forceLogout) {
        credentials.force_logout = true;
      }

      try {
        const result = await login(credentials);
        if (result?.two_factor_required) {
          this.showOtpStep = true;
          this.loginApi.showLoading = false;
          return;
        }
        this.handleImpersonation();
        this.sessionConflict = null;
        this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data || {};

        // 409 single-session: NO recargamos la página ni mostramos alerta
        // normal; en vez de eso dejamos el form listo para reintento con
        // el botón "Cerrar sesión en el otro dispositivo".
        if (status === 409 && data.error === 'session_already_active') {
          this.loginApi.showLoading = false;
          this.sessionConflict = {
            message:
              data.message || this.$t('LOGIN.API.SESSION_ALREADY_ACTIVE'),
          };
          return;
        }

        if (this.email) {
          window.location = '/app/login';
        }
        this.loginApi.hasErrored = true;
        // Clear the OTP code so the user can retype without manually erasing
        if (this.showOtpStep) {
          this.otpCode = '';
        }
        const serverErrors = data.errors;
        const serverError = data.error;
        // rev54 — si el backend mandó un código conocido (ej. account_deactivated),
        // usar la traducción del i18n del frontend en vez del código literal.
        // Antes mostrábamos `data.error` directo y para algunos códigos del
        // backend (como Devise i18n con interpolación rota) se filtraba el
        // raw template "%{authentication_keys}/password o cuenta no válida…".
        const errorCodeMessage = ERROR_MESSAGES[serverError]
          ? this.$t(ERROR_MESSAGES[serverError])
          : null;
        const message =
          (Array.isArray(serverErrors) ? serverErrors[0] : null) ||
          errorCodeMessage ||
          data.message ||
          serverError ||
          this.$t('LOGIN.API.UNAUTH');
        this.showAlertMessage(message);
      }
    },
    confirmForceLogout() {
      // Reintentar el mismo submit pero pasando force_logout=true. El backend
      // revoca las sesiones anteriores del usuario y emite la nueva.
      this.submitLogin({ forceLogout: true });
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      if (this.isDarkMode) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('login_theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('login_theme', 'light');
      }
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
  },
};
</script>

<template>
  <main class="flex w-full min-h-screen">
    <!-- Lado izquierdo: gradiente corporativo -->
    <div
      class="hidden lg:block lg:w-3/5 bg-login-gradient bg-login-banner bg-fixed animate-gradient-shift"
    />

    <!-- Lado derecho: formulario -->
    <div
      class="w-full lg:w-2/5 flex flex-col items-center justify-center bg-white dark:bg-gov-dark px-6 sm:px-12 py-12 relative transition-colors duration-300"
    >
      <!-- Toggle tema (esquina superior derecha) -->
      <button
        type="button"
        class="absolute top-4 right-4 p-2 rounded-full text-slate-300 dark:text-slate-200 hover:text-gov-navy dark:hover:text-gov-gold transition-colors"
        :aria-label="
          isDarkMode ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'
        "
        @click="toggleDarkMode"
      >
        <!-- Sol (dark mode activo) -->
        <svg
          v-if="isDarkMode"
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 3v1m0 16v1m-8-9H3m16 0h1m-2.636-6.364l-.707.707M6.343 17.657l-.707.707m12.021 0l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z"
          />
        </svg>
        <!-- Luna (light mode activo) -->
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z"
          />
        </svg>
      </button>

      <!-- Logo gobierno -->
      <div class="mb-10">
        <img
          :src="globalConfig.logo"
          :alt="globalConfig.installationName"
          class="block w-auto h-28 mx-auto dark:hidden"
        />
        <img
          v-if="globalConfig.logoDark"
          :src="globalConfig.logoDark"
          :alt="globalConfig.installationName"
          class="hidden w-auto h-28 mx-auto dark:block"
        />
      </div>

      <!-- Título -->
      <h1 class="text-3xl font-extrabold text-gov-navy dark:text-white mb-10">
        {{ $t('LOGIN.WELCOME') }}
      </h1>

      <!-- Signup link -->
      <p
        v-if="showSignupLink"
        class="mb-6 text-sm text-slate-300 dark:text-slate-200"
      >
        {{ $t('COMMON.OR') }}
        <router-link
          to="auth/signup"
          class="lowercase text-gov-navy dark:text-gov-gold font-semibold underline"
        >
          {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
        </router-link>
      </p>

      <!-- Formulario directo -->
      <div
        v-if="!email"
        class="w-full max-w-sm"
        :class="{ 'animate-wiggle': loginApi.hasErrored }"
      >
        <!-- Single-session conflict: backend rechazó login por sesión activa -->
        <div
          v-if="sessionConflict"
          role="alert"
          class="mb-6 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="login_session_conflict"
        >
          <div class="flex items-start gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 shrink-0 mt-0.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p class="leading-snug m-0">{{ sessionConflict.message }}</p>
          </div>
          <NextButton
            sm
            type="button"
            class="self-start"
            :label="$t('LOGIN.API.FORCE_LOGOUT_OTHER_SESSION')"
            :disabled="loginApi.showLoading"
            :is-loading="loginApi.showLoading"
            @click="confirmForceLogout"
          />
        </div>

        <!-- Banner persistente para errores que requieren explicación -->
        <div
          v-if="persistentNotice"
          role="alert"
          class="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="login_persistent_notice"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 shrink-0 mt-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p class="leading-snug m-0">{{ $t(persistentNotice.messageKey) }}</p>
        </div>

        <GoogleOAuthButton v-if="showGoogleOAuth && !showOtpStep" />

        <!-- Paso 2FA -->
        <form
          v-if="showOtpStep"
          class="space-y-6"
          @submit.prevent="submitLogin"
        >
          <div class="text-center mb-2">
            <div
              class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gov-navy/10 dark:bg-gov-gold/10 mb-3"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-6 h-6 text-gov-navy dark:text-gov-gold"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('LOGIN.TWO_FACTOR.DESCRIPTION') }}
            </p>
          </div>
          <div class="text-center">
            <label
              class="block text-xs font-semibold text-slate-500 dark:text-slate-300 mb-3 uppercase tracking-wider"
            >
              {{ $t('LOGIN.TWO_FACTOR.CODE_LABEL') }}
            </label>
            <OtpInput
              v-model="otpCode"
              variant="light"
              :error="loginApi.hasErrored"
              @complete="submitLogin"
            />
          </div>
          <NextButton
            lg
            type="submit"
            class="w-full login-btn"
            :tabindex="2"
            :label="$t('LOGIN.TWO_FACTOR.VERIFY_BTN')"
            :disabled="loginApi.showLoading || otpCode.length < 6"
            :is-loading="loginApi.showLoading"
          />
          <button
            type="button"
            class="w-full text-sm text-slate-500 dark:text-slate-300 hover:text-gov-navy dark:hover:text-gov-gold transition-colors"
            @click="
              showOtpStep = false;
              otpCode = '';
            "
          >
            {{ $t('LOGIN.TWO_FACTOR.BACK_BTN') }}
          </button>
        </form>

        <!-- Paso normal: email + contraseña -->
        <form v-else class="space-y-6" @submit.prevent="submitFormLogin">
          <FormInput
            v-model="credentials.email"
            name="email_address"
            type="text"
            data-testid="email_input"
            :tabindex="1"
            required
            :label="$t('LOGIN.USER_LABEL')"
            :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
            :has-error="v$.credentials.email.$error"
            @input="v$.credentials.email.$touch"
          />
          <FormInput
            v-model="credentials.password"
            type="password"
            name="password"
            data-testid="password_input"
            required
            :tabindex="2"
            :label="$t('LOGIN.PASSWORD_LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
            :has-error="v$.credentials.password.$error"
            @input="v$.credentials.password.$touch"
          />
          <NextButton
            lg
            type="submit"
            data-testid="submit_button"
            class="w-full login-btn"
            :tabindex="3"
            :label="$t('LOGIN.SIGN_IN')"
            :aria-label="$t('LOGIN.SUBMIT')"
            :disabled="loginApi.showLoading"
            :is-loading="loginApi.showLoading"
          />
          <div class="text-center">
            <router-link
              to="auth/reset/password"
              class="text-sm text-slate-400 dark:text-slate-300 hover:text-gov-navy dark:hover:text-gov-gold transition-colors"
            >
              {{ $t('LOGIN.FORGOT_PASSWORD') }}
            </router-link>
          </div>
        </form>
      </div>
      <div v-else class="flex items-center justify-center py-8">
        <Spinner color-scheme="primary" size="" />
      </div>

      <!-- Footer -->
      <p class="mt-auto pt-8 text-sm text-slate-300 dark:text-slate-200">
        {{ $t('LOGIN.POWERED_BY') }}
        <a
          href="https://wetalks.app/"
          target="_blank"
          rel="noopener noreferrer"
          class="font-bold text-gov-navy dark:text-gov-gold hover:underline"
        >
          {{ $t('LOGIN.BRAND_NAME') }}
        </a>
      </p>
    </div>
  </main>
</template>

<style scoped>
.login-btn {
  background-color: #1c1e4d !important;
  border-color: #1c1e4d !important;
  color: #fff !important;
}
.login-btn:hover {
  background-color: #313945 !important;
  border-color: #313945 !important;
}
</style>

<style>
/* Dark mode login button - unscoped to access .dark on html */
html.dark .login-btn {
  background-color: #c9a892 !important;
  border-color: #c9a892 !important;
  color: #1c1e4d !important;
}
html.dark .login-btn:hover {
  background-color: #b8956f !important;
  border-color: #b8956f !important;
}
</style>
